import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FormGroup, ValidationErrors } from '@angular/forms';
import { Directory, Filesystem, WriteFileResult } from '@capacitor/filesystem';
import { FileOpener } from '@ionic-native/file-opener/ngx';
import * as EmailValidator from 'email-validator';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { EquipmentUserDefinedFieldLabelsPayload } from '../models/equipment/equipment-user-defined-field-labels-payload';
import { ReturnMessage } from '../models/return-message';
import { PostGetPayCodes } from '../models/shared/post-get-pay-codes';
import { ResponseGetApplicationTypes } from '../models/shared/response-get-application-types';
import { ResponsePayCodes } from '../models/shared/response-pay-codes';
import { UserDataService } from './user-data/user-data.service';

@Injectable({
  providedIn: 'root',
})
export class UtilityService {

  private apis = {
    getApplicationTypes: 'utility/GetApplicationTypes/',
    autocompleteUserDefinedStatuses: 'utility/AutocompleteRecommendationUserDefinedStatus/',
    getPayCodes: 'utility/GetPayCodes/',
    autocompleteJobTitle: 'utility/AutocompleteJobTitle/',
    autocompleteSalesPersonId: 'utility/AutocompleteSalesPersonId/',
    autocompleteQuoteTemplate: 'utility/AutocompleteQuoteTemplate/',
    autocompleteQuoteDocumentTemplate: 'utility/AutocompleteQuoteDocumentTemplate/',
    getEquipmentUserDefinedFieldLabels: 'utility/GetEquipmentUserDefinedFieldLabels/',
  }


  constructor(private http: HttpClient,
    private userDataService: UserDataService,
    private fileOpener: FileOpener) { }

  validateEmailAddressField(emailAddressField) {
    //QU-15915 - allow multiple email addresses to be in the emailaddress field, and they can be separated by commas or semi-colons.
    //first replace all commas with semi-colons, then split the string by semicolons and validate each email address
    var emailAddressFieldIsValid = true;
    var emailAddressString = emailAddressField.replace(/,/g, ';');
    var emailAddressList = emailAddressString.split(';').map((e) => e.trim());
    var errorMessage = '';
    for (let email of emailAddressList) {
      if (email != undefined && email != null && email.trim() != '') {
        if (!EmailValidator.validate(email)) {
          emailAddressFieldIsValid = false;
          errorMessage = 'The email address [' + email + '] is not valid';
          break;
        }
      }
    }
    return {
      validEmailAddressField: emailAddressFieldIsValid,
      errorMessage: errorMessage,
    };

  }

  download(url: string) {
    return this.http.get(url, {
      responseType: 'blob',
      headers: new HttpHeaders({
        isFullUrl: 'true' // used by the http interceptor to know if it should preappend the gateway base URL to the request URL
      })
    });
  }

  convertBlobToBase64 = (blob: Blob) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader;
      reader.onerror = reject;
      reader.onload = () => {
        resolve(reader.result);
      };
      reader.readAsDataURL(blob);
    });

  async createDocumentsFolderIfNotExist() {
    try {
      await Filesystem.mkdir({
        path: environment.MOBILE_DOCUMENTS_FOLDER,
        recursive: true,
        directory: Directory.Documents
      });
    } catch(e) {
      console.log(e);
    }
  }

  async doesFileExist(fileName: string) {
    const readDirResult = await Filesystem.readdir({
      path: environment.MOBILE_DOCUMENTS_FOLDER,
      directory: Directory.Documents
    });
    return readDirResult.files.some(f => { 
      return f.toLocaleLowerCase() === fileName.toLocaleLowerCase(); 
    });
  }

  async CheckDuplicatedName(fileName: string) {
    let assignedFileName = fileName;
    let exists = false;
    let copyNumber = 2;
    do {
      exists = await this.doesFileExist(assignedFileName);
      if (exists) {
        assignedFileName = `(${copyNumber}) ${fileName}`;
        copyNumber++;
      }
    } while (exists);
    return assignedFileName;
  }

  async saveFile(fileName: string, base64data: string) {
    await this.createDocumentsFolderIfNotExist();
    let assignedFileName = await this.CheckDuplicatedName(fileName);
    return await Filesystem.writeFile({
      path: `${environment.MOBILE_DOCUMENTS_FOLDER}/${assignedFileName}`,
      data: base64data,
      directory: Directory.Documents
    });
  }

  async saveBinaryDataAsFile(fileName: string, blob: Blob): Promise<WriteFileResult> {
    const base64data = await this.convertBlobToBase64(blob) as string;
    return await this.saveFile(fileName, base64data);
  }

  async saveBase64DataAsFile(fileName: string, base64data: string): Promise<WriteFileResult> {
    return await this.saveFile(fileName, base64data);
  }

  async getFileBlob(uri: string) {
    return await fetch(uri).then((r) => r.blob());
  }

  async getBase64DataAsBlob(base64Data: string) {
    const response = await fetch(base64Data);
    return await response.blob();
  }

  checkRegex(regexToUse: RegExp, varToTestAgainst: string): boolean {
    varToTestAgainst = varToTestAgainst.replace(/\r?\n|\r/g, '');
    return regexToUse.test(varToTestAgainst);
  }

  removeDollarAmountCharacters(value: string) {
    return value.replace('$', '').replace(/,/g, '').replace(/\r?\n|\r/g, '');
  }

  parseDollarAmount(value: string): number {
    return Number.parseFloat(this.removeDollarAmountCharacters(value));
  }

  removePercentCharacters(value: string) {
    return value.replace('%', '');
  }

  parsePercentage(value: string): number {
    return Number.parseFloat(this.removePercentCharacters(value));
  }

  getApplicationTypes(): Observable<ReturnMessage<ResponseGetApplicationTypes>> {
    return this.http.get<ReturnMessage<ResponseGetApplicationTypes>>(this.apis.getApplicationTypes);
  }

  getPayCodes(data: PostGetPayCodes): Observable<ReturnMessage<ResponsePayCodes>> {
    return this.http.post<ReturnMessage<ResponsePayCodes>>(this.apis.getPayCodes, data);
  }

  async openFileDownload(fileName: string, blob: Blob, fileMIMEType: string) {
    if (this.userDataService.isMobilePlatform()) {
      const savedFile = await this.saveBinaryDataAsFile(fileName, blob);
      await this.fileOpener.open(savedFile.uri, fileMIMEType);
    }
    else {
      const a = document.createElement('a');
      document.body.appendChild(a);
      const url = window.URL.createObjectURL(blob);
      a.href = url;
      a.download = fileName;
      a.click();
      window.URL.revokeObjectURL(url);
    }
  }

  logFormValidationErrors(form: FormGroup) {
    Object.keys(form.controls).forEach(key => {
      const controlErrors: ValidationErrors = form.get(key).errors;
      if (controlErrors != null) {
        Object.keys(controlErrors).forEach(keyError => {
          console.log('Key control: ' + key + ', keyError: ' + keyError + ', err value: ', controlErrors[keyError]);
        });
      }
    });
  }

  getEquipmentUserDefinedFieldLabels(): Observable<ReturnMessage<EquipmentUserDefinedFieldLabelsPayload>> {
    return this.http.get<ReturnMessage<EquipmentUserDefinedFieldLabelsPayload>>(this.apis.getEquipmentUserDefinedFieldLabels);
  }
}

